/**
 * "Troubleshooting" — Guides section (launch-readiness B8).
 *
 * Symptom-first answers to the failure modes we have actually seen (QA harness
 * + production): a strict host-page CSP blocking the widget's injected styles,
 * the cold-start first-request hiccup, framework detection misses, enrichment
 * / offline situations, sign-in problems, and consent-decision changes. Every
 * remedy here is the real, currently-shipping one — no aspirational flags
 * (the CLI's `--init-config` message bug is documented honestly on the init
 * page and tracked as a CLI fix).
 */
import type { DocPageContent } from '../types';

export const GUIDES_TROUBLESHOOTING_PAGE: DocPageContent = {
  slug: 'guides/troubleshooting',
  titleKey: 'docs.guides.troubleshooting.title',
  descriptionKey: 'docs.guides.troubleshooting.description',
  blocks: [
    { kind: 'paragraph', textKey: 'docs.guides.troubleshooting.lead' },

    {
      kind: 'heading',
      level: 2,
      id: 'widget-missing',
      textKey: 'docs.guides.troubleshooting.widget.heading',
    },
    { kind: 'paragraph', textKey: 'docs.guides.troubleshooting.widget.csp' },
    { kind: 'paragraph', textKey: 'docs.guides.troubleshooting.widget.script' },
    { kind: 'paragraph', textKey: 'docs.guides.troubleshooting.widget.trustedTypes' },

    {
      kind: 'heading',
      level: 2,
      id: 'first-request',
      textKey: 'docs.guides.troubleshooting.cold.heading',
    },
    { kind: 'paragraph', textKey: 'docs.guides.troubleshooting.cold.body' },

    {
      kind: 'heading',
      level: 2,
      id: 'detection',
      textKey: 'docs.guides.troubleshooting.detect.heading',
    },
    { kind: 'paragraph', textKey: 'docs.guides.troubleshooting.detect.body' },
    { kind: 'paragraph', textKey: 'docs.guides.troubleshooting.detect.ignore' },

    {
      kind: 'heading',
      level: 2,
      id: 'enrichment',
      textKey: 'docs.guides.troubleshooting.enrich.heading',
    },
    { kind: 'paragraph', textKey: 'docs.guides.troubleshooting.enrich.body' },

    {
      kind: 'heading',
      level: 2,
      id: 'auth',
      textKey: 'docs.guides.troubleshooting.auth.heading',
    },
    { kind: 'paragraph', textKey: 'docs.guides.troubleshooting.auth.body' },

    {
      kind: 'heading',
      level: 2,
      id: 'consent',
      textKey: 'docs.guides.troubleshooting.consent.heading',
    },
    { kind: 'paragraph', textKey: 'docs.guides.troubleshooting.consent.body' },

    {
      kind: 'callout',
      tone: 'note',
      titleKey: 'docs.guides.troubleshooting.stuck.title',
      textKey: 'docs.guides.troubleshooting.stuck.body',
    },

    { kind: 'heading', level: 2, id: 'next', textKey: 'docs.common.nextSteps' },
    {
      kind: 'cardGrid',
      cards: [
        {
          titleKey: 'docs.guides.troubleshooting.related.doctor.title',
          textKey: 'docs.guides.troubleshooting.related.doctor.text',
          to: { docSlug: 'cli/commands' },
        },
        {
          titleKey: 'docs.guides.troubleshooting.related.widget.title',
          textKey: 'docs.guides.troubleshooting.related.widget.text',
          to: { docSlug: 'widget' },
        },
      ],
    },
  ],
};
